import React, { forwardRef, useMemo, useRef, type ReactNode } from "react";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import { useDashlyOverlayAppendTo } from "../shared/resolveDashlyOverlayAppendTo";
import { DashlyControlSize, dashlyControlSizeClass } from "../shared/dashlyControlSize";
import { DashlyControlVariant, dashlyControlVariantClass } from "../shared/dashlyControlVariant";
import { DashlyLeadingIcon, DashlyLeadingIconSlot, resolveLeadingIconClass } from "../shared/resolveLeadingIcon";
import "../shared/dashlyFormControl.scss";
import "./DashlyDropdown.scss";

export type DashlyDropdownProps = Omit<DropdownProps, "filter"> & {
    /** Enable panel search. Defaults to false. */
    filter?: boolean;
    filterPlaceholder?: string;
    /** Static PrimeIcons class or resolver from current value. */
    leadingIcon?: DashlyLeadingIcon;
    /** `filter` — list-page filter bar styling. */
    controlVariant?: DashlyControlVariant;
    /** Control height scale. Default `md` (2.5rem). */
    controlSize?: DashlyControlSize;
    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    containerClassName?: string;
    /**
     * PrimeReact's Dropdown has no native HTML readonly (only disabled), so a merely-locked field
     * would otherwise fall back to the washed-out disabled look. Set this instead of `disabled` when
     * the field should stay legible (dark text) while still blocking interaction — same intent as
     * `readOnly` on DashlyTextInput/DashlyTextarea.
     */
    readOnly?: boolean;
};

const DashlyDropdown = forwardRef<Dropdown, DashlyDropdownProps>(function DashlyDropdown(
    {
        leadingIcon,
        filter = false,
        filterPlaceholder,
        controlVariant = "form",
        controlSize = "md",
        label,
        labelInfo,
        hint,
        required,
        containerClassName,
        className,
        panelClassName,
        dropdownIcon = "pi pi-chevron-down",
        valueTemplate,
        itemTemplate,
        value,
        inputId,
        id,
        appendTo: appendToProp,
        showClear = true,
        filterInputAutoFocus,
        readOnly = false,
        disabled,
        ...rest
    },
    ref
) {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const overlayAppendTo = useDashlyOverlayAppendTo(wrapperRef);
    const resolvedAppendTo = appendToProp ?? overlayAppendTo;
    const resolvedInputId = inputId ?? id;
    const resolvedValue = value === "" || value === undefined ? null : value;
    const leadingIconClass = resolveLeadingIconClass(leadingIcon, resolvedValue);

    const resolvedItemTemplate = useMemo(() => {
        if (itemTemplate) {
            return itemTemplate;
        }
        if (!leadingIcon) {
            return undefined;
        }
        return (option: unknown) => {
            const iconClass = resolveLeadingIconClass(leadingIcon, option);
            const labelText = rest.optionLabel
                ? (option as Record<string, unknown>)?.[rest.optionLabel as string]
                : String(option ?? "");
            return (
                <span className="dashly-dropdown__item">
                    <DashlyLeadingIconSlot iconClass={iconClass} />
                    <span>{labelText as ReactNode}</span>
                </span>
            );
        };
    }, [itemTemplate, leadingIcon, rest.optionLabel]);

    const control = (
        <div
            ref={wrapperRef}
            className={classNames(
                "dashly-dropdown-wrap",
                dashlyControlSizeClass(controlSize),
                leadingIconClass && "dashly-dropdown-wrap--with-icon",
            )}
        >
            {leadingIconClass ? (
                <DashlyLeadingIconSlot
                    iconClass={leadingIconClass}
                    className="dashly-dropdown-wrap__leading-icon"
                />
            ) : null}
            <Dropdown
                ref={ref}
                {...rest}
                id={resolvedInputId}
                inputId={resolvedInputId}
                value={resolvedValue}
                disabled={disabled || readOnly}
                showClear={readOnly ? false : showClear}
                filter={filter}
                filterPlaceholder={filterPlaceholder}
                filterInputAutoFocus={filterInputAutoFocus ?? filter}
                dropdownIcon={dropdownIcon}
                valueTemplate={valueTemplate}
                itemTemplate={resolvedItemTemplate}
                className={classNames(
                    dashlyControlVariantClass("dashly-dropdown", controlVariant),
                    "w-full",
                    readOnly && "is-readonly",
                    className,
                )}
                panelClassName={classNames(
                    controlVariant === "filter" ? "dashly-filter-dropdown-panel" : "dashly-dropdown-panel",
                    panelClassName,
                )}
                appendTo={resolvedAppendTo}
            />
        </div>
    );

    if (!label) {
        return control;
    }

    return (
        <DashlyFieldShell
            label={label}
            labelInfo={labelInfo}
            hint={hint}
            htmlFor={resolvedInputId}
            required={required}
            className={containerClassName}
        >
            {control}
        </DashlyFieldShell>
    );
});

DashlyDropdown.displayName = "DashlyDropdown";

export { DashlyDropdown };
